import { useEffect } from "react";
import { useLocation } from "react-router";

import { usePacks } from "./PackContext";
import { isSettingsGroupRoute } from "../../views/dashboard/sidebar/routeMapping";
import { ROUTES } from "../shared/constant";

/**
 * Keeps the view mode (Sales / Marketing) in step with the URL.
 *
 * ⚠️ THE ASYMMETRY THIS CLOSES. `PackRouteGuard` sets `operator_crm` when a
 * /crm/* route mounts, and NOTHING did the reverse. So the mode only ever moved
 * one way: land on any CRM page and the shell becomes Sales; deep-link to an
 * Advisor page afterwards and the shell stays Sales, because the persisted mode
 * is never revisited.
 *
 * Reported against /ad-campaigns: opening it directly rendered the CRM sidebar
 * around a Marketing page, with no nav item selected — the page is not in the
 * CRM sidebar at all, so there was nothing to highlight. The URL said Marketing
 * and the chrome said Sales.
 *
 * A deep link, a bookmark and a shared link are all this shape: they set the
 * route without going through the toggle, which is the only other thing that
 * moves the mode. Mode is therefore derived from the route — the URL is the
 * single source of truth for which half of the product you are in.
 *
 * `setCurrentMode` is already a no-op when `!canToggleMode`, so single-pack
 * tenants (the five provisioned Advisor tenants, and any CRM-only tenant) are
 * untouched by this: there is no second mode for them to be moved into.
 *
 * ⚠️ SETTINGS-GROUP ROUTES ARE NEUTRAL, AND MUST STAY THAT WAY. Deriving mode
 * from "is this /crm/*?" alone was too blunt: the shared platform pages —
 * Users, Roles, Channels, Call Management, Activity Logs, Notifications,
 * Session, Configurations — live outside /crm/*, so opening one DEMOTED the
 * operator to Marketing and PERSISTED it. The CRM Setup hub links straight to
 * Users and Roles, so the most ordinary CRM admin errand silently moved the
 * user into the other half of the product and left them there; they had to
 * notice and toggle back. Those pages belong to neither half, so visiting one
 * must not express an opinion about which half you are in.
 *
 * The asymmetry above is still closed for routes that genuinely ARE Marketing
 * (/ad-campaigns and friends). Neutral means "leave the mode alone", not
 * "assume Sales" — an Advisor user opening Users stays in Marketing, exactly as
 * a CRM user opening it stays in Sales.
 *
 * ⚠️ HELP + SUPPORT IS NEUTRAL TOO, AND IS NOT A SETTINGS-GROUP ROUTE. It is
 * rendered in its own "Support" section by BOTH shells — `composeCrmModeSections`
 * appends the shared Settings AND Support sections to the pack's own — so one
 * ticket page serves Sales and Marketing alike. It is not in
 * `SETTINGS_GROUP_NAV_IDS`, and must not be added there: that list also answers
 * "which nav category is active?" (routeMapping.getParentCategoryId), so joining
 * it would re-parent a top-level Support item under Settings and expand a
 * Settings menu with nothing highlighted in it.
 *
 * So it gets its own neutral case here, exactly like /crm/profile below. Without
 * it, a Sales operator clicking Help + Support was demoted to Marketing and left
 * there — the ticket page loaded inside the Marketing shell, which reads as
 * "Help + Support sent me to the Marketing page".
 */
export function ModeFollowsRoute() {
  const { pathname } = useLocation();
  const { setCurrentMode } = usePacks();

  const isCrmRoute = pathname === "/crm" || pathname.startsWith("/crm/");
  // The shared self-serve My Profile page (/crm/profile) is a neutral platform
  // surface reachable from BOTH views — visiting it must NOT flip the mode, or a
  // dual-pack advisor-view user would be switched into CRM view just by opening
  // their profile (Workstream B D-VP-4; mirrors the ProfileRouteGuard that also
  // deliberately skips setCurrentMode).
  const isProfileRoute =
    pathname === ROUTES.CRM_PROFILE || pathname.startsWith(ROUTES.CRM_PROFILE + "/");
  // Help + Support — shared Support-section surface, reachable from both shells.
  // Both of its routes count: HELPPLUSSUPPORT is the list and QUERYANDISSUES the
  // ticket view, and ROUTE_PATTERNS already maps the pair to one nav id.
  const isHelpSupportRoute = [ROUTES.HELPPLUSSUPPORT, ROUTES.QUERYANDISSUES].some(
    (base) => pathname === base || pathname.startsWith(base + "/"),
  );
  const isNeutralRoute = isSettingsGroupRoute(pathname) || isProfileRoute || isHelpSupportRoute;

  useEffect(() => {
    if (isNeutralRoute) return;
    setCurrentMode(isCrmRoute ? "operator_crm" : "lead_gen_only");
  }, [isCrmRoute, isNeutralRoute, setCurrentMode]);

  return null;
}
